import { forwardRef } from 'react';
import type {
  HTMLAttributes,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * Tabela de dados (estilo shadcn/ui "new-york", escrita à mão pelo mesmo motivo
 * do `Card`: o CLI precisa de rede). Markup puro, sem Radix — não é dependência
 * nova, e a regra 13 continua satisfeita.
 *
 * ── `border-separate`, e não `border-collapse` ────────────────────────────
 *
 * Não é preferência estética: com `border-collapse` o navegador ignora
 * `position: sticky` em `<th>`, e o cabeçalho que gruda ao rolar é metade do
 * que torna uma tabela longa legível. O preço é que a divisória mora em cada
 * CÉLULA (`border-b`), nunca na `<tr>` — uma borda declarada na linha
 * simplesmente não pinta aqui.
 *
 * ── Sem moldura e sem `overflow` em volta ─────────────────────────────────
 *
 * A tabela é a superfície: linhas separadas por um fio, sem caixa ao redor.
 * Envolvê-la num `overflow-x-auto` seria a solução óbvia para telas estreitas
 * e está errada duas vezes — o contêiner de rolagem passaria a ser o
 * referencial do `sticky` (e `overflow-x: auto` liga o eixo Y junto, pela
 * própria especificação), e rolar a tabela na horizontal esconde coluna sem
 * dizer que escondeu. Quem resolve a tela estreita é a coluna que se retira
 * sozinha (`hidden sm:table-cell`), não a barra de rolagem.
 */

export const Table = forwardRef<HTMLTableElement, TableHTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn('w-full border-separate border-spacing-0 text-sm', className)}
      {...props}
    />
  ),
);
Table.displayName = 'Table';

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={className} {...props} />);
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={className} {...props} />);
TableBody.displayName = 'TableBody';

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => <tr ref={ref} className={className} {...props} />,
);
TableRow.displayName = 'TableRow';

/**
 * Célula de cabeçalho, grudada no topo.
 *
 * `top-14` acompanha a altura da faixa superior do `AppShell` (`h-14`), que já
 * é `sticky top-0` — sem o deslocamento o cabeçalho da tabela pararia embaixo
 * dela e sumiria. `z-20` fica abaixo do `z-30` daquela faixa, para a tabela
 * deslizar sob a moldura, e não por cima.
 *
 * ⚠️ **Opaco, e a primeira versão era translúcida.** A ideia era a das barras
 * do sistema — conteúdo passando por baixo de um material desfocado, em vez de
 * cortado por uma divisória dura. Numa tabela densa aquilo não funciona: as
 * linhas continuam legíveis atrás do rótulo da coluna, e o que se vê não é
 * profundidade, é colisão — duas linhas de texto ocupando a mesma faixa de
 * pixels. Material translúcido pressupõe um fundo que não compete; um `<td>`
 * cheio de nome de projeto compete.
 */
export const TableHead = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      'sticky top-14 z-20 h-9 border-b border-border bg-card px-3 text-left align-middle',
      // `leading-none` para o rótulo de duas palavras ("Tempo de espera") caber
      // em duas linhas DENTRO dos 36px. `h-9` numa célula de tabela é altura
      // MÍNIMA, não fixa: sem isto, um cabeçalho que quebra estica a faixa e
      // desalinha o `top` de quem gruda embaixo dela — o cabeçalho de mês da
      // tabela de projetos está preso a essa altura (`top-[5.75rem]` = 3.5rem
      // da faixa superior + 2.25rem daqui).
      'text-xs font-medium uppercase leading-none tracking-wide text-muted-foreground',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

export const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('border-b border-border px-3 py-2.5 align-middle', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';
